import { createColumnHelper } from '@tanstack/react-table';
import _ from 'lodash';
import { CalendarClock, ChevronRight } from 'lucide-react';
import { fromZonedTime } from 'date-fns-tz';
import { formatDistanceToNow } from 'date-fns';

import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import {
  formatDate,
  parseEnvironmentTimezone,
} from '@/react/portainer/environments/update-schedules/common/utils';

import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { IconButton } from '@@/buttons/IconButton';
import { Icon } from '@@/Icon';

import { ScheduleEnvironment } from './types';
import { envStatus } from './envStatus';
import { duration } from './utils';

const columnHelper = createColumnHelper<ScheduleEnvironment>();

export function getColumns(
  setSelectedItem: (value: ((prevState: number) => number) | number) => void
) {
  return _.compact([
    columnHelper.accessor('environment.Name', {
      header: '',
      cell: ({ getValue, row: { original: item } }) => (
        <div className="flex flex-col justify-between h-full p-2">
          <div className="text-base font-medium">{getValue()}</div>
          {timeUntilStart(
            item.created,
            item.scheduledTime,
            item.environment.LocalTimeZone
          )}
        </div>
      ),
      enableHiding: false,
      enableSorting: false,
    }),
    isBE &&
      columnHelper.accessor('id', {
        header: '',
        cell: ({ row: { original: item } }) => (
          <div className="flex flex-col justify-between h-full items-end p-2">
            <div className="!text-base">{envStatus(item)}</div>
            <div className="flex">{duration(item)}</div>
          </div>
        ),
        enableHiding: false,
        enableSorting: false,
      }),
    isBE &&
      columnHelper.accessor('name', {
        header: '',
        enableSorting: false,
        cell: ({ getValue, row: { original } }) => (
          <div className="flex gap-3 justify-end items-center h-full p-2">
            <div>
              <IconButton
                icon={ChevronRight}
                size="large"
                data-cy={`edit-schedule-link-${getValue()}`}
                to=""
                onClick={() => setSelectedItem(original.environment.Id)}
              />
            </div>
          </div>
        ),
        enableHiding: false,
      }),
  ]);
}

function timeUntilStart(
  createdTime: number,
  scheduledTime: string,
  localTimeZone?: string
) {
  if (!createdTime || !scheduledTime || !localTimeZone) return null;

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const environmentTimeZone = parseEnvironmentTimezone(localTimeZone);
  const createdDate = new Date(createdTime * 1000);
  const localCreatedDate = fromZonedTime(createdDate, timeZone);
  const zonedScheduledTime = fromZonedTime(scheduledTime, environmentTimeZone);
  const localScheduledTime = fromZonedTime(zonedScheduledTime, timeZone);

  const startDate =
    localCreatedDate > localScheduledTime
      ? localCreatedDate
      : localScheduledTime;
  const duration = getDuration(startDate);
  const humanDistance = formatDistanceToNow(startDate, { addSuffix: true });

  const formattedScheduledDateWithOffset = formatDate(localScheduledTime);
  const formattedStartDateWithOffset = formatDate(startDate);

  if (duration <= 0) {
    return (
      <div className="flex">
        <TooltipWithChildren
          position="right"
          message={`Started ${formattedStartDateWithOffset}`}
        >
          <div className="flex gap-2 items-center">
            <Icon icon={CalendarClock} />
            <div>{formattedScheduledDateWithOffset}</div>
          </div>
        </TooltipWithChildren>
      </div>
    );
  }

  return (
    <div className="flex">
      <TooltipWithChildren
        position="right"
        message={`Starts: ${formattedStartDateWithOffset}`}
      >
        <div className="flex gap-2 items-center">
          <Icon icon={CalendarClock} />
          <div>Starts {humanDistance}</div>
        </div>
      </TooltipWithChildren>
    </div>
  );
}

function getDuration(scheduledTime: string | Date, localTimeZone?: string) {
  let date: Date;
  if (localTimeZone) {
    date = fromZonedTime(scheduledTime, localTimeZone);
  } else {
    date = new Date(scheduledTime);
  }

  const now = new Date();

  return date.getTime() - now.getTime();
}
